import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import './GalleryAdmin.css';

const API_BASE_URL = 'https://api.avtechbmt.cloud/api/v1/gallery';
const IMAGE_BASE_URL = 'https://api.avtechbmt.cloud';

/* Inline styles for the checkbox — cannot be hidden by any CSS file */
const CHECKBOX_CELL_STYLE = {
  width: 52,
  minWidth: 52,
  maxWidth: 52,
  textAlign: 'center',
  padding: '12px 8px',
  display: 'table-cell',
  visibility: 'visible',
  opacity: 1,
  verticalAlign: 'middle',
};

const CHECKBOX_STYLE = {
  width: 18,
  height: 18,
  cursor: 'pointer',
  accentColor: '#c74e2b',
  verticalAlign: 'middle',
  display: 'inline-block',
  visibility: 'visible',
  opacity: 1,
  margin: 0,
  appearance: 'auto',
  WebkitAppearance: 'checkbox',
};

export default function GalleryAdmin() {
  const { token } = useAuth();

  // ----------------------------- Data state -----------------------------
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [bulkWorking, setBulkWorking] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [stats, setStats] = useState(null);

  // ----------------------------- Form state -----------------------------
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('General');
  const [description, setDescription] = useState('');
  const [order, setOrder] = useState(0);
  const [isActive, setIsActive] = useState(true);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [formOpen, setFormOpen] = useState(false);

  // ----------------------------- Selection -----------------------------
  const [selectedIds, setSelectedIds] = useState(new Set());

  // ----------------------------- Filters -----------------------------
  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortBy, setSortBy] = useState('order');
  const [sortDir, setSortDir] = useState('asc');

  // ----------------------------- Lightbox -----------------------------
  const [lightbox, setLightbox] = useState(null);

  // ----------------------------- Bulk delete confirm modal -------------
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  const authToken = token || localStorage.getItem('token');

  // ----------------------------- Fetch -----------------------------
  const fetchGalleryItems = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE_URL}/admin/all?limit=500`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });

      const contentType = res.headers.get('content-type') || '';
      if (!contentType.includes('application/json')) {
        throw new Error(`Server returned ${res.status} (non-JSON response)`);
      }

      const data = await res.json();
      if (!res.ok) throw new Error(data.message || `Failed to fetch (${res.status})`);

      const list = Array.isArray(data) ? data : Array.isArray(data.data) ? data.data : [];
      setItems(list);

      if (list.length === 0) {
        setError('No gallery items returned by the server.');
      }
    } catch (err) {
      setItems([]);
      setError(err.message || 'Failed to fetch gallery items');
    } finally {
      setLoading(false);
    }
  }, [authToken]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/admin/stats`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const contentType = res.headers.get('content-type') || '';
      if (!contentType.includes('application/json')) return;
      const data = await res.json();
      if (res.ok && data && data.data) setStats(data.data);
    } catch {
      /* non-fatal */
    }
  }, [authToken]);

  useEffect(() => {
    fetchGalleryItems();
    fetchStats();
  }, [fetchGalleryItems, fetchStats]);

  // Auto-dismiss success toast
  useEffect(() => {
    if (!success) return;
    const t = setTimeout(() => setSuccess(''), 3500);
    return () => clearTimeout(t);
  }, [success]);

  // ----------------------------- Derived -----------------------------
  const categories = useMemo(() => {
    const set = new Set(items.map((i) => i.category || 'Uncategorized'));
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [items]);

  const filteredItems = useMemo(() => {
    let out = [...items];

    if (search.trim()) {
      const q = search.toLowerCase();
      out = out.filter(
        (i) =>
          (i.title || '').toLowerCase().includes(q) ||
          (i.description || '').toLowerCase().includes(q)
      );
    }
    if (filterCategory !== 'all') {
      out = out.filter((i) => (i.category || 'Uncategorized') === filterCategory);
    }
    if (filterStatus === 'active') out = out.filter((i) => i.isActive);
    if (filterStatus === 'hidden') out = out.filter((i) => !i.isActive);

    const dir = sortDir === 'asc' ? 1 : -1;
    out.sort((a, b) => {
      const va = a[sortBy];
      const vb = b[sortBy];

      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;

      if (typeof va === 'number' && typeof vb === 'number') {
        return (va - vb) * dir;
      }
      return String(va).localeCompare(String(vb)) * dir;
    });

    return out;
  }, [items, search, filterCategory, filterStatus, sortBy, sortDir]);

  const allVisibleSelected =
    filteredItems.length > 0 && filteredItems.every((i) => selectedIds.has(i._id));
  const someSelected = selectedIds.size > 0;

  // ----------------------------- Helpers -----------------------------
  const getImageUrl = (path) => {
    if (!path) return '';
    const raw = String(path).replace(/\\/g, '/');

    if (/^https?:\/\//i.test(raw)) return raw;

    const idx = raw.indexOf('uploads/');
    const rel = idx !== -1 ? raw.substring(idx) : raw.replace(/^\/+/, '');
    return `${IMAGE_BASE_URL}/${rel}`;
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (allVisibleSelected) {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        filteredItems.forEach((i) => next.delete(i._id));
        return next;
      });
    } else {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        filteredItems.forEach((i) => next.add(i._id));
        return next;
      });
    }
  };

  const clearSelection = () => setSelectedIds(new Set());

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(field);
      setSortDir('asc');
    }
  };

  const SortIcon = ({ field }) => {
    if (sortBy !== field) return <span className="sort-icon">↕</span>;
    return <span className="sort-icon active">{sortDir === 'asc' ? '↑' : '↓'}</span>;
  };

  // ----------------------------- Form handlers -----------------------------
  const resetForm = () => {
    setTitle('');
    setCategory('General');
    setDescription('');
    setOrder(0);
    setIsActive(true);
    setImageFile(null);
    setImagePreview(null);
    setEditingId(null);
  };

  const openCreateForm = () => {
    resetForm();
    setFormOpen(true);
    setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 50);
  };

  const closeForm = () => {
    resetForm();
    setFormOpen(false);
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSubmitting(true);

    const formData = new FormData();
    formData.append('title', title);
    formData.append('category', category);
    formData.append('description', description);
    formData.append('order', order);
    formData.append('isActive', isActive);
    if (imageFile) formData.append('image', imageFile);

    try {
      let res;
      if (editingId) {
        res = await fetch(`${API_BASE_URL}/${editingId}`, {
          method: 'PUT',
          headers: { Authorization: `Bearer ${authToken}` },
          body: formData,
        });
      } else {
        if (!imageFile) {
          setError('An image file is required to create a gallery item.');
          setSubmitting(false);
          return;
        }
        res = await fetch(API_BASE_URL, {
          method: 'POST',
          headers: { Authorization: `Bearer ${authToken}` },
          body: formData,
        });
      }

      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Action failed');

      setSuccess(editingId ? 'Item updated successfully.' : 'New image uploaded successfully.');
      closeForm();
      fetchGalleryItems();
      fetchStats();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (item) => {
    setEditingId(item._id);
    setTitle(item.title);
    setCategory(item.category);
    setDescription(item.description || '');
    setOrder(item.order || 0);
    setIsActive(item.isActive);
    setImageFile(null);
    setImagePreview(getImageUrl(item.imageUrl));
    setFormOpen(true);
    setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 50);
  };

  // ----------------------------- Single actions -----------------------------
  const handleDelete = async (id) => {
    if (!window.confirm('Permanently delete this item?')) return;
    setError('');
    setSuccess('');
    try {
      const res = await fetch(`${API_BASE_URL}/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to delete item');
      setSuccess('Item deleted.');
      setSelectedIds((prev) => {
        const n = new Set(prev);
        n.delete(id);
        return n;
      });
      fetchGalleryItems();
      fetchStats();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleToggleActive = async (item) => {
    setError('');
    try {
      const res = await fetch(`${API_BASE_URL}/${item._id}/toggle`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to toggle');
      setItems((prev) =>
        prev.map((i) => (i._id === item._id ? { ...i, isActive: data.data.isActive } : i))
      );
      fetchStats();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDuplicate = async (item) => {
    setError('');
    try {
      const res = await fetch(`${API_BASE_URL}/${item._id}/duplicate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to duplicate');
      setSuccess('Item duplicated (hidden by default).');
      fetchGalleryItems();
      fetchStats();
    } catch (err) {
      setError(err.message);
    }
  };

  // ----------------------------- Bulk actions -----------------------------
  const bulkSetVisibility = async (isActiveValue) => {
    if (selectedIds.size === 0) return;
    setBulkWorking(true);
    setError('');
    setSuccess('');
    try {
      const res = await fetch(`${API_BASE_URL}/bulk/visibility`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({
          ids: Array.from(selectedIds),
          isActive: isActiveValue,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Bulk update failed');
      setSuccess(data.message || `${selectedIds.size} item(s) updated.`);
      setSelectedIds(new Set());
      fetchGalleryItems();
      fetchStats();
    } catch (err) {
      setError(err.message);
    } finally {
      setBulkWorking(false);
    }
  };

  const openBulkDeleteConfirm = () => {
    if (selectedIds.size === 0) return;
    setConfirmDeleteOpen(true);
  };

  const closeBulkDeleteConfirm = () => {
    if (bulkWorking) return;
    setConfirmDeleteOpen(false);
  };

  const confirmBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    setBulkWorking(true);
    setError('');
    setSuccess('');
    try {
      const res = await fetch(`${API_BASE_URL}/bulk`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({ ids: Array.from(selectedIds) }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Bulk delete failed');
      setSuccess(data.message || `${selectedIds.size} item(s) deleted.`);
      setSelectedIds(new Set());
      setConfirmDeleteOpen(false);
      fetchGalleryItems();
      fetchStats();
    } catch (err) {
      setError(err.message);
    } finally {
      setBulkWorking(false);
    }
  };

  const bulkChangeCategory = async (newCategory) => {
    if (selectedIds.size === 0 || !newCategory) return;
    setBulkWorking(true);
    setError('');
    setSuccess('');
    try {
      const res = await fetch(`${API_BASE_URL}/bulk/category`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({
          ids: Array.from(selectedIds),
          category: newCategory,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Bulk category change failed');
      setSuccess(data.message || `${selectedIds.size} item(s) moved.`);
      setSelectedIds(new Set());
      fetchGalleryItems();
      fetchStats();
    } catch (err) {
      setError(err.message);
    } finally {
      setBulkWorking(false);
    }
  };

  // =========================================================================
  // RENDER
  // =========================================================================
  return (
    <div className="gallery-admin-wrapper">
      {/* -------------------- Header -------------------- */}
      <div className="ga-header">
        <div>
          <h2 className="profile-title">Gallery Control</h2>
          <p className="profile-subtitle">
            Upload, organize, and curate your visual library
          </p>
        </div>
        <button className="cta-primary" onClick={openCreateForm}>
          <span className="plus">＋</span> New Image
        </button>
      </div>

      {/* -------------------- Stats -------------------- */}
      {stats && (
        <div className="ga-stats">
          <div className="ga-stat-card">
            <div className="ga-stat-value">{stats.total}</div>
            <div className="ga-stat-label">Total</div>
          </div>
          <div className="ga-stat-card">
            <div className="ga-stat-value ga-stat-active">{stats.active}</div>
            <div className="ga-stat-label">Active</div>
          </div>
          <div className="ga-stat-card">
            <div className="ga-stat-value ga-stat-hidden">{stats.inactive}</div>
            <div className="ga-stat-label">Hidden</div>
          </div>
          <div className="ga-stat-card">
            <div className="ga-stat-value">{stats.byCategory?.length || 0}</div>
            <div className="ga-stat-label">Categories</div>
          </div>
        </div>
      )}

      {/* -------------------- Notifications -------------------- */}
      {error && (
        <div className="auth-error" style={{ marginBottom: '1.25rem' }}>
          <span>✕</span> {error}
        </div>
      )}
      {success && (
        <div className="auth-success" style={{ marginBottom: '1.25rem' }}>
          <span>✓</span> {success}
        </div>
      )}

      {/* -------------------- Form (collapsible) -------------------- */}
      {formOpen && (
        <div className="metric-card ga-form-card">
          <div className="ga-form-header">
            <span className="field-label" style={{ color: 'var(--text-primary)' }}>
              {editingId ? '✦ EDIT GALLERY ITEM' : '✦ UPLOAD NEW ITEM'}
            </span>
            <button
              type="button"
              className="ga-icon-btn"
              onClick={closeForm}
              title="Close"
            >
              ✕
            </button>
          </div>

          <form onSubmit={handleSubmit} className="ga-form">
            <div className="form-grid-3">
              <div className="form-group">
                <label className="field-label">TITLE</label>
                <input
                  type="text"
                  required
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g., Modern Architecture"
                />
              </div>
              <div className="form-group">
                <label className="field-label">CATEGORY</label>
                <input
                  type="text"
                  required
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  placeholder="e.g., Campus, Design"
                  list="category-suggestions"
                />
                <datalist id="category-suggestions">
                  {categories.map((c) => (
                    <option key={c} value={c} />
                  ))}
                </datalist>
              </div>
              <div className="form-group">
                <label className="field-label">DISPLAY ORDER</label>
                <input
                  type="number"
                  value={order}
                  onChange={(e) => setOrder(Number(e.target.value))}
                />
              </div>
            </div>

            <div className="form-group">
              <label className="field-label">DESCRIPTION</label>
              <textarea
                rows="2"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Provide context or narrative for this piece..."
              />
            </div>

            <div className="form-grid-2-bottom">
              <div className="form-group">
                <label className="field-label">MEDIA ATTACHMENT</label>
                <div className="dropzone-container">
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={handleFileChange}
                    className="dropzone-input"
                  />
                  {imagePreview ? (
                    <div className="dropzone-preview">
                      <img src={imagePreview} alt="Preview" />
                      <div>
                        <div style={{ fontSize: '0.85rem', fontWeight: '600' }}>
                          {imageFile ? imageFile.name : 'Current Image Attached'}
                        </div>
                        <div
                          style={{
                            fontSize: '0.75rem',
                            color: 'var(--text-muted)',
                          }}
                        >
                          Click to change file
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <div style={{ fontSize: '0.85rem', fontWeight: '600' }}>
                        Drop image here or click to browse
                      </div>
                      <div
                        style={{
                          fontSize: '0.75rem',
                          color: 'var(--text-muted)',
                          marginTop: '0.25rem',
                        }}
                      >
                        PNG, JPG or WEBP accepted
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="form-group">
                <label
                  className="field-label"
                  style={{ marginBottom: '0.5rem' }}
                >
                  VISIBILITY
                </label>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={isActive}
                    onChange={(e) => setIsActive(e.target.checked)}
                  />
                  <span className="slider"></span>
                  <span style={{ fontSize: '0.8rem', fontWeight: '600' }}>
                    {isActive ? 'Public' : 'Hidden'}
                  </span>
                </label>
              </div>
            </div>

            <div className="form-actions">
              <button
                type="submit"
                className="cta-primary-admin-image-submit"
                disabled={submitting}
              >
                {submitting
                  ? 'Processing...'
                  : editingId
                  ? 'Save Changes'
                  : 'Upload Image'}
              </button>
              <button
                type="button"
                className="cta-secondary"
                onClick={closeForm}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* -------------------- Toolbar -------------------- */}
      <div className="ga-toolbar">
        <div className="ga-toolbar-left">
          <div className="ga-search">
            <span className="ga-search-icon">⌕</span>
            <input
              type="text"
              placeholder="Search title or description…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && (
              <button
                className="ga-search-clear"
                onClick={() => setSearch('')}
              >
                ✕
              </button>
            )}
          </div>

          <select
            className="ga-select"
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
          >
            <option value="all">All categories</option>
            {categories.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>

          <select
            className="ga-select"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="all">All status</option>
            <option value="active">Active only</option>
            <option value="hidden">Hidden only</option>
          </select>
        </div>

        <div className="ga-toolbar-right">
          <span className="ga-count">
            {filteredItems.length} / {items.length}
          </span>
        </div>
      </div>

      {/* -------------------- Bulk action bar -------------------- */}
      <div className={`ga-bulk-bar ${someSelected ? 'ga-bulk-bar--active' : ''}`}>
        <div className="ga-bulk-info">
          <label className="ga-bulk-selectall">
            <input
              type="checkbox"
              checked={allVisibleSelected}
              onChange={toggleSelectAll}
              aria-label="Select all visible"
              style={CHECKBOX_STYLE}
            />
            <span>
              {someSelected
                ? `${selectedIds.size} selected`
                : `Select all (${filteredItems.length})`}
            </span>
          </label>
          {someSelected && (
            <button className="ga-link-btn" onClick={clearSelection}>
              Clear
            </button>
          )}
        </div>

        <div className="ga-bulk-actions">
          <button
            className="ga-bulk-btn"
            onClick={() => bulkSetVisibility(true)}
            disabled={bulkWorking || !someSelected}
            title="Make selected items visible on the public site"
          >
            👁 Show
          </button>
          <button
            className="ga-bulk-btn"
            onClick={() => bulkSetVisibility(false)}
            disabled={bulkWorking || !someSelected}
            title="Hide selected items from the public site"
          >
            🚫 Hide
          </button>
          <select
            className="ga-select ga-bulk-select"
            defaultValue=""
            onChange={(e) => {
              if (e.target.value) bulkChangeCategory(e.target.value);
              e.target.value = '';
            }}
            disabled={bulkWorking || !someSelected}
            title="Move selected items to a category"
          >
            <option value="">Move to…</option>
            {categories.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
          <button
            className="ga-bulk-btn ga-bulk-danger"
            onClick={openBulkDeleteConfirm}
            disabled={bulkWorking || !someSelected}
            title="Delete selected items"
          >
            🗑 Delete{someSelected ? ` (${selectedIds.size})` : ''}
          </button>
        </div>
      </div>

      {/* -------------------- Table / Card view -------------------- */}
      <div className="admin-table-container">
        {loading ? (
          <div className="table-message">Retrieving gallery assets…</div>
        ) : filteredItems.length === 0 ? (
          <div className="table-message">
            {items.length === 0
              ? 'No gallery items found. Create one above to populate your inventory.'
              : 'No items match the current filters.'}
          </div>
        ) : (
          <table className="admin-table">
            <thead>
              <tr>
                <th style={CHECKBOX_CELL_STYLE}>
                  <input
                    type="checkbox"
                    checked={allVisibleSelected}
                    onChange={toggleSelectAll}
                    aria-label="Select all"
                    style={CHECKBOX_STYLE}
                  />
                </th>
                <th>PREVIEW</th>
                <th>
                  <button
                    className="ga-sort-btn"
                    onClick={() => handleSort('title')}
                  >
                    TITLE <SortIcon field="title" />
                  </button>
                </th>
                <th>
                  <button
                    className="ga-sort-btn"
                    onClick={() => handleSort('category')}
                  >
                    CATEGORY <SortIcon field="category" />
                  </button>
                </th>
                <th>
                  <button
                    className="ga-sort-btn"
                    onClick={() => handleSort('order')}
                  >
                    ORDER <SortIcon field="order" />
                  </button>
                </th>
                <th>STATUS</th>
                <th style={{ textAlign: 'right' }}>ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {filteredItems.map((item) => {
                const isSelected = selectedIds.has(item._id);
                return (
                  <tr
                    key={item._id}
                    className={isSelected ? 'ga-row-selected' : ''}
                  >
                    <td style={CHECKBOX_CELL_STYLE}>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleSelect(item._id)}
                        aria-label={`Select ${item.title}`}
                        style={CHECKBOX_STYLE}
                      />
                    </td>
                    <td className="ga-preview-col">
                      <img
                        src={getImageUrl(item.imageUrl)}
                        alt={item.title}
                        className="gallery-thumb"
                        onClick={() => setLightbox(item)}
                        loading="lazy"
                        onError={(e) => {
                          e.currentTarget.style.opacity = '0.3';
                          e.currentTarget.alt = 'Image failed to load';
                        }}
                      />
                    </td>
                    <td className="ga-title-col">
                      <div className="gallery-title-cell">{item.title}</div>
                      {item.description && (
                        <div className="gallery-desc-cell">
                          {item.description}
                        </div>
                      )}
                    </td>
                    <td className="ga-category-col">
                      <span className="role-badge user">{item.category}</span>
                    </td>
                    <td className="ga-order-col">{item.order}</td>
                    <td className="ga-status-col">
                      <button
                        className={`status-pill ${
                          item.isActive ? 'active' : 'hidden'
                        }`}
                        onClick={() => handleToggleActive(item)}
                        title="Click to toggle visibility"
                      >
                        <span className="dot" />
                        {item.isActive ? 'Active' : 'Hidden'}
                      </button>
                    </td>
                    <td className="gallery-actions-cell ga-actions-col">
                      <button
                        className="cta-secondary gallery-edit-btn"
                        onClick={() => handleEdit(item)}
                      >
                        Edit
                      </button>
                      <button
                        className="cta-secondary gallery-edit-btn"
                        onClick={() => handleDuplicate(item)}
                        title="Duplicate"
                      >
                        ⧉
                      </button>
                      <button
                        className="btn-action-delete"
                        onClick={() => handleDelete(item._id)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* -------------------- Bulk delete confirmation modal -------------------- */}
      {confirmDeleteOpen && (
        <div className="ga-modal-backdrop" onClick={closeBulkDeleteConfirm}>
          <div className="ga-modal-card" onClick={(e) => e.stopPropagation()}>
            <h3 className="ga-modal-title">
              Delete {selectedIds.size} image{selectedIds.size === 1 ? '' : 's'}?
            </h3>
            <p className="ga-modal-body">
              This will permanently remove <strong>{selectedIds.size}</strong>{' '}
              gallery item{selectedIds.size === 1 ? '' : 's'} and their image
              {selectedIds.size === 1 ? '' : 's'} from the server. This action
              cannot be undone.
            </p>
            <div className="ga-modal-actions">
              <button
                type="button"
                className="cta-secondary"
                onClick={closeBulkDeleteConfirm}
                disabled={bulkWorking}
              >
                Cancel
              </button>
              <button
                type="button"
                className="ga-modal-danger-btn"
                onClick={confirmBulkDelete}
                disabled={bulkWorking}
              >
                {bulkWorking ? 'Deleting…' : `Delete ${selectedIds.size}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* -------------------- Lightbox -------------------- */}
      {lightbox && (
        <div className="ga-lightbox" onClick={() => setLightbox(null)}>
          <div
            className="ga-lightbox-inner"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={getImageUrl(lightbox.imageUrl)}
              alt={lightbox.title}
            />
            <div className="ga-lightbox-caption">
              <strong>{lightbox.title}</strong>
              <span>{lightbox.category}</span>
            </div>
            <button
              className="ga-lightbox-close"
              onClick={() => setLightbox(null)}
            >
              ✕
            </button>
          </div>
        </div>
      )}
    </div>
  );
}